import { z } from 'zod';
import { id, paginatedResult, paginationQuery } from './common.js';
import { profession } from './professional.js';
import { bookingMode } from './shift.js';
import { shiftView } from './shift-view.js';

export const shiftSearchSort = z.enum(['nearest', 'soonest', 'highest_pay']);
export type ShiftSearchSort = z.infer<typeof shiftSearchSort>;

/** Short-lived browser-tab state, kept out of shareable search URLs. */
export const deviceSearchOrigin = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  expiresAt: z.number().finite(),
});
export type DeviceSearchOrigin = z.infer<typeof deviceSearchOrigin>;

const dateBoundary = z.union([
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((value) => {
      const date = new Date(`${value}T00:00:00Z`);
      return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
    }, 'Enter a valid calendar date'),
  z.string().datetime({ offset: true }),
]);
const queryNumber = (schema: z.ZodType<number>) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? Number.NaN : value),
    schema
  );

/** HTTP query contract: geographic origins are explicit, never inferred from a text search. */
export const shiftSearchQuery = paginationQuery
  .extend({
    role: profession.optional(),
    locationId: id.optional(),
    from: dateBoundary.optional(),
    to: dateBoundary.optional(),
    eligible: z.enum(['true', 'false']).optional(),
    search: z.string().trim().min(1).max(100).optional(),
    minRateCents: queryNumber(z.coerce.number().int().min(0).max(1_000_000)).optional(),
    radiusMiles: queryNumber(z.coerce.number().positive().max(500)).optional(),
    latitude: queryNumber(z.coerce.number().min(-90).max(90)).optional(),
    longitude: queryNumber(z.coerce.number().min(-180).max(180)).optional(),
    originPostalCode: z
      .string()
      .regex(/^\d{5}$/, 'Enter a five-digit US ZIP code')
      .optional(),
    sort: shiftSearchSort.default('nearest'),
    bookingMode: bookingMode.optional(),
  })
  .superRefine((query, context) => {
    if ((query.latitude === undefined) !== (query.longitude === undefined)) {
      context.addIssue({
        code: 'custom',
        message: 'Latitude and longitude must be supplied together',
        path: ['latitude'],
      });
    }
    if (query.originPostalCode && query.latitude !== undefined) {
      context.addIssue({
        code: 'custom',
        message: 'Choose either a ZIP origin or device coordinates',
        path: ['originPostalCode'],
      });
    }
    if (!query.from || !query.to) return;
    const reversed =
      query.from.length === 10 && query.to.length === 10
        ? query.from > query.to
        : query.from.length > 10 &&
          query.to.length > 10 &&
          Date.parse(query.from) > Date.parse(query.to);
    if (reversed)
      context.addIssue({
        code: 'custom',
        message: 'From date must be on or before the to date',
        path: ['to'],
      });
  });

/** Typed domain/client filter; eligibleOnly is the app-facing name of HTTP's eligible flag. */
export type ShiftSearchFilter = Partial<
  Omit<z.infer<typeof shiftSearchQuery>, 'page' | 'pageSize' | 'eligible'>
> & {
  eligibleOnly?: boolean;
};

export const shiftSearchContext = z.object({
  origin: z.enum(['browser', 'profile', 'postal', 'none']),
  postalCode: z.string().nullable(),
  distanceKind: z.literal('straight_line'),
  radiusMiles: z.number().nullable(),
  sort: shiftSearchSort,
  unknownDistanceCount: z.number().int().nonnegative(),
  excludedUnknownDistanceCount: z.number().int().nonnegative(),
});
export type ShiftSearchContext = z.infer<typeof shiftSearchContext>;
export const shiftSearchPage = paginatedResult(shiftView).extend({ search: shiftSearchContext });
export type ShiftSearchPage = z.infer<typeof shiftSearchPage>;
